import React, { useState, useEffect, useMemo } from 'react';
import {
  FileText, Users, BarChart3, Sparkles, TrendingUp, Target,
  ChevronDown, ChevronRight, AlertTriangle, CheckCircle, Clock
} from 'lucide-react';
import {
  RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis,
  Radar, ResponsiveContainer, Legend,
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip
} from 'recharts';
import { useAuth } from '../../lib/AuthContext';
import {
  getClassAnalytics, getAssignments, getDrafts, getMyClasses, getClassMembers,
  getStudentProgress,
} from '../../lib/api';
import { DraftyMascot } from '../DraftyMascot';
import { CCSS_ELA_STANDARDS } from '../../lib/standards';
import { BRAND } from '../../lib/brandColors';
import { studentLabel } from '../../lib/format';

/**
 * Determine the anchor standard code for a given standard string.
 * e.g. "W.9-10.1a" -> "W.9-10.1", "W.9-10.1" -> "W.9-10.1"
 */
const getAnchorCode = (code) => {
  if (!code) return code;
  if (code.length > 8 && code[code.length - 1].match(/[a-z]/)) {
    return code.slice(0, -1);
  }
  return code;
};

/**
 * Group radarData items by anchor standard.
 */
const groupByAnchor = (radarData) => {
  const groups = {};

  for (const item of radarData) {
    const anchor = getAnchorCode(item.standard);
    if (!groups[anchor]) {
      groups[anchor] = { anchorData: null, subItems: [] };
    }
    if (item.standard === anchor) {
      groups[anchor].anchorData = item;
    } else {
      groups[anchor].subItems.push(item);
    }
  }

  return groups;
};

const ProgressBar = ({ value, color }) => (
  <div className="flex items-center gap-2">
    <div className="w-24 h-2 bg-slate-700 rounded-full overflow-hidden">
      <div
        className={`h-full ${color} rounded-full`}
        style={{ width: `${value || 0}%` }}
      />
    </div>
    <span className={`${color === 'bg-teal-flow' ? 'text-teal-flow' : 'text-gold-bright'} text-sm font-mono w-10 text-right`}>
      {value != null ? `${value}%` : '--'}
    </span>
  </div>
);

const StandardRow = ({ label, mastery, needsWork, indent = false, muted = false }) => (
  <div className={`flex items-center justify-between p-3 ${indent ? 'ml-6' : ''} bg-slate-900/50 rounded-lg border border-slate-700`}>
    <span className={`${indent ? 'text-xs font-mono' : 'badge-ccss'} ${muted ? 'text-slate-500' : ''}`}>{label}</span>
    {muted ? (
      <span className="text-slate-500 text-xs italic">No data yet</span>
    ) : (
      <div className="flex items-center gap-6">
        <ProgressBar value={mastery} color="bg-teal-flow" />
        <ProgressBar value={needsWork} color="bg-gold-bright" />
      </div>
    )}
  </div>
);

// ==================== Insights panels (Phase 6 item 4) ====================

// Declared ABOVE its consumers on purpose (D23): module constants a component
// reads should never sit below that component in the file.
const STAGE_LABEL = {
  prewrite: 'Prewrite',
  draft: 'Draft',
  revise: 'Revise',
  edit: 'Edit',
  publish: 'Publish',
};

/**
 * Standards ranked by how often feedback flagged something to work on.
 *
 * Only rows the coach tagged `grow` are growth signals. A whole-pass
 * `comprehensive` row carries both a glow and a grow but names ONE specific
 * gap, so counting it per-standard would over-attribute — those are reported
 * as coaching passes instead, and the empty state says so plainly.
 */
const GrowthAreas = ({ areas }) => {
  const ranked = (areas || []).filter(a => a.grow > 0).slice(0, 6);
  const coached = (areas || []).reduce((sum, a) => sum + (a.coached || 0), 0);

  return (
    <div className="card-surface p-6" data-testid="insights-growth-areas">
      <h3 className="font-heading text-lg font-semibold text-white mb-4 flex items-center gap-2">
        <Target className="text-gold-bright" size={20} />
        Most-common growth areas
      </h3>
      {ranked.length === 0 ? (
        <p className="text-slate-400 text-sm">
          {coached > 0
            ? `No standard has been flagged for growth yet. The coach has made ${coached} coaching ${coached === 1 ? 'pass' : 'passes'} so far. Growth areas rank here once feedback is tagged to a standard.`
            : 'Growth areas will rank here as students receive coaching feedback on their writing.'}
        </p>
      ) : (
        <div className="space-y-2">
          {ranked.map((area) => (
            <div
              key={area.standard}
              className="flex items-center justify-between p-3 bg-slate-900/50 rounded-lg border border-slate-700"
            >
              <div className="flex items-center gap-3">
                <span className="badge-ccss">{area.standard}</span>
                <span className="text-slate-400 text-xs">
                  {area.students} {area.students === 1 ? 'student' : 'students'}
                </span>
              </div>
              <div className="flex items-center gap-6">
                <span className="text-gold-bright text-sm font-mono">
                  {area.grow} to work on
                </span>
                <ProgressBar value={area.growth_share} color="bg-gold-bright" />
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

/**
 * Student x standard grid. Colour matches the radar legend exactly — teal is
 * strength, gold is growth — so the two charts teach one visual language.
 * A cell with no feedback stays empty rather than showing a zero: no feedback
 * is not the same as poor work.
 */
const HeatCell = ({ cell, label }) => {
  if (!cell) {
    return <td className="p-1"><div className="w-7 h-7 rounded border border-slate-700/60" title={`${label}: no feedback yet`} /></td>;
  }
  const graded = (cell.glow || 0) + (cell.grow || 0);
  if (graded === 0) {
    return (
      <td className="p-1">
        <div
          className="w-7 h-7 rounded bg-slate-700/60 flex items-center justify-center"
          title={`${label}: ${cell.coached} coaching ${cell.coached === 1 ? 'pass' : 'passes'}, not tagged to a standard`}
        >
          <span className="w-1.5 h-1.5 rounded-full bg-slate-400" />
        </div>
      </td>
    );
  }
  const masteryShare = cell.glow / graded;
  const strong = masteryShare >= 0.5;
  const intensity = strong ? masteryShare : 1 - masteryShare;
  return (
    <td className="p-1">
      <div
        className={`w-7 h-7 rounded ${strong ? 'bg-teal-flow' : 'bg-gold-bright'}`}
        style={{ opacity: 0.25 + intensity * 0.6 }}
        title={`${label}: ${cell.glow} strength, ${cell.grow} to work on`}
      />
    </td>
  );
};

const StandardsHeatmap = ({ heatmap }) => {
  const standards = heatmap?.standards || [];
  const students = heatmap?.students || [];

  if (standards.length === 0 || students.length === 0) {
    return (
      <div className="card-surface p-6" data-testid="insights-heatmap">
        <h3 className="font-heading text-lg font-semibold text-white mb-4 flex items-center gap-2">
          <BarChart3 className="text-teal-flow" size={20} />
          Standards by student
        </h3>
        <p className="text-slate-400 text-sm">
          Every student's standards picture appears here once they start receiving feedback.
        </p>
      </div>
    );
  }

  return (
    <div className="card-surface p-6" data-testid="insights-heatmap">
      <h3 className="font-heading text-lg font-semibold text-white mb-1 flex items-center gap-2">
        <BarChart3 className="text-teal-flow" size={20} />
        Standards by student
      </h3>
      <p className="text-slate-400 text-sm mb-4">
        Where each student is strong, and where the feedback keeps pointing.
      </p>
      <div className="overflow-x-auto">
        <table className="text-xs">
          <thead>
            <tr>
              <th className="text-left text-slate-400 font-normal pr-3 pb-2">Student</th>
              {standards.map(std => (
                <th key={std} className="pb-2 px-1">
                  <span className="block text-slate-400 font-mono text-[10px] whitespace-nowrap">{std}</span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {students.map(student => (
              <tr key={student.student_id}>
                <td className="text-slate-300 pr-3 whitespace-nowrap max-w-[10rem] truncate" title={studentLabel(student)}>
                  {studentLabel(student)}
                </td>
                {standards.map(std => (
                  <HeatCell
                    key={std}
                    cell={student.cells?.[std]}
                    label={`${studentLabel(student)}, ${std}`}
                  />
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="flex items-center gap-4 mt-4 text-xs text-slate-500">
        <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded bg-teal-flow" /> Strength</span>
        <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded bg-gold-bright" /> Growth area</span>
        <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded bg-slate-700/60" /> Coached, untagged</span>
        <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded border border-slate-700/60" /> No feedback yet</span>
      </div>
    </div>
  );
};

/**
 * One student across assignments — the growth story a conference or a report
 * card conversation needs. Wires the existing progress endpoint, which had no
 * caller at all before this.
 */
const StudentTrajectory = ({ students }) => {
  const [studentId, setStudentId] = useState('');
  const [progress, setProgress] = useState(null);
  const [loading, setLoading] = useState(false);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (!studentId) { setProgress(null); return undefined; }
    let cancelled = false;
    setLoading(true);
    setFailed(false);
    getStudentProgress(studentId)
      .then(data => { if (!cancelled) setProgress(data?.progress || []); })
      .catch(e => {
        console.error('Failed to load student trajectory:', e);
        if (!cancelled) { setProgress([]); setFailed(true); }
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [studentId]);

  const chartData = (progress || []).map((point, i) => ({
    name: point.assignment_title || `Assignment ${i + 1}`,
    words: point.word_count || 0,
  }));

  return (
    <div className="card-surface p-6" data-testid="insights-trajectory">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <h3 className="font-heading text-lg font-semibold text-white flex items-center gap-2">
          <TrendingUp className="text-ai-accent" size={20} />
          One student over time
        </h3>
        <select
          aria-label="Choose a student to see their trajectory"
          value={studentId}
          onChange={(e) => setStudentId(e.target.value)}
          className="bg-slate-700 border border-slate-600 text-slate-300 text-xs rounded-lg px-2 py-1.5 cursor-pointer"
        >
          <option value="">Choose a student…</option>
          {(students || []).map(s => (
            <option key={s.student_id} value={s.student_id}>{studentLabel(s)}</option>
          ))}
        </select>
      </div>

      {!studentId ? (
        <p className="text-slate-400 text-sm">
          Pick a student to see their writing across every assignment they've worked on.
        </p>
      ) : loading ? (
        <p className="text-slate-500 text-sm">Loading their history…</p>
      ) : failed ? (
        <p className="text-slate-400 text-sm">Couldn't load this student's history. Try again in a moment.</p>
      ) : (progress || []).length === 0 ? (
        <p className="text-slate-400 text-sm">No drafts yet for this student.</p>
      ) : (
        <div className="space-y-4">
          <div className="h-48">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={chartData}>
                <CartesianGrid stroke="#334155" strokeDasharray="3 3" />
                <XAxis dataKey="name" tick={{ fill: BRAND.textSecondaryDark, fontSize: 11 }} />
                <YAxis tick={{ fill: '#64748b', fontSize: 11 }} />
                <Tooltip
                  contentStyle={{ background: '#0f172a', border: '1px solid #334155', borderRadius: 8, color: '#fff' }}
                  formatter={(value) => [`${value} words`, 'Length']}
                />
                <Line type="monotone" dataKey="words" stroke={BRAND.tealFlow} strokeWidth={2} dot={{ r: 3 }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
          <div className="space-y-2">
            {progress.map(point => (
              <div
                key={point.draft_id}
                className="flex flex-wrap items-center justify-between gap-2 p-3 bg-slate-900/50 rounded-lg border border-slate-700"
              >
                <div className="flex items-center gap-3 min-w-0">
                  <span className="text-white text-sm truncate">{point.assignment_title}</span>
                  {(point.standard_codes || []).slice(0, 3).map(std => (
                    <span key={std} className="badge-ccss">{std}</span>
                  ))}
                </div>
                <div className="flex items-center gap-3 text-xs">
                  <span className="text-slate-400">{point.word_count} words</span>
                  <span className={`status-chip ${point.status === 'submitted' ? 'status-chip--primary' : 'status-chip--gold'}`}>
                    {point.status === 'submitted' ? 'Submitted' : STAGE_LABEL[point.stage] || 'In progress'}
                  </span>
                  {point.date && (
                    <span className="text-slate-500">{new Date(point.date).toLocaleDateString()}</span>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// ==================== Tab Components ====================


export const DraftDataAnalytics = () => {
  const { profile } = useAuth();
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedAnchors, setExpandedAnchors] = useState({});
  const [status, setStatus] = useState(null);

  useEffect(() => {
    const loadAnalytics = async () => {
      try {
        const data = await getClassAnalytics(profile?.id);
        setAnalytics(data);
      } catch (error) {
        console.error('Failed to load analytics:', error);
      } finally {
        setLoading(false);
      }
    };
    loadAnalytics();
  }, [profile]);

  // The four questions a teacher actually asks: who hasn't started,
  // who's stuck, who submitted — derived client-side from data the
  // teacher already has access to (RLS-scoped drafts + own rosters).
  useEffect(() => {
    if (!profile?.id) return;
    let cancelled = false;
    const loadStatus = async () => {
      try {
        const [assignmentsData, draftsData, classesData] = await Promise.all([
          getAssignments(profile.id),
          getDrafts(),
          getMyClasses(),
        ]);
        const classes = Array.isArray(classesData) ? classesData : classesData?.classes || [];
        const rosterByClass = {};
        await Promise.all(classes.map(async (c) => {
          try {
            const m = await getClassMembers(c.join_code);
            rosterByClass[c.id] = m?.members || [];
          } catch {
            rosterByClass[c.id] = [];
          }
        }));
        if (cancelled) return;

        const active = (assignmentsData || []).filter(a => a.status === 'active');
        const drafts = draftsData || [];
        const now = Date.now();
        const STUCK_MS = 72 * 60 * 60 * 1000;

        const perAssignment = active.map(a => {
          const aDrafts = drafts.filter(d => d.assignment_id === a.id);
          const startedIds = new Set(aDrafts.map(d => d.student_id));
          const roster = a.class_id ? (rosterByClass[a.class_id] || []) : [];
          const notStarted = roster.filter(m => !startedIds.has(m.student_id));
          const submitted = aDrafts.filter(d => d.status === 'submitted');
          return { assignment: a, notStarted, submitted, total: aDrafts.length };
        });

        const stuck = drafts
          .filter(d => d.status !== 'submitted')
          .map(d => {
            const last = d.last_saved || d.created_at;
            return { ...d, idleMs: last ? now - new Date(last).getTime() : null };
          })
          .filter(d => d.idleMs !== null && d.idleMs > STUCK_MS)
          .sort((x, y) => y.idleMs - x.idleMs)
          .map(d => ({
            ...d,
            idleDays: Math.floor(d.idleMs / (24 * 60 * 60 * 1000)),
            assignmentTitle: active.find(a => a.id === d.assignment_id)?.title || 'Assignment',
          }));

        setStatus({ perAssignment, stuck });
      } catch (e) {
        console.error('Failed to load class status:', e);
        if (!cancelled) setStatus({ perAssignment: [], stuck: [], error: true });
      }
    };
    loadStatus();
    return () => { cancelled = true; };
  }, [profile]);

  const radarData = analytics?.standards_breakdown?.length > 0
    ? analytics.standards_breakdown
    : [];

  // mastery/needs_work are raw glow/grow COUNTS from the API, not percentages.
  // Render the true ratio: glow / (glow + grow) across all standards.
  const totalGlow = radarData.reduce((sum, d) => sum + (d.mastery || 0), 0);
  const totalGrow = radarData.reduce((sum, d) => sum + (d.needs_work || 0), 0);
  const avgMastery = totalGlow + totalGrow > 0
    ? Math.round((totalGlow / (totalGlow + totalGrow)) * 100)
    : null;

  const toggleAnchorExpand = (anchor) => {
    setExpandedAnchors(prev => ({ ...prev, [anchor]: !prev[anchor] }));
  };

  if (loading) {
    return <div className="text-center py-12 text-slate-400">Loading...</div>;
  }

  const grouped = groupByAnchor(radarData);

  return (
    <div className="space-y-6">
      <div>
        <h2 className="font-heading text-2xl font-bold text-white flex items-center gap-3">
          <BarChart3 className="text-teal-flow" size={28} />
          Insights
        </h2>
        <p className="text-slate-400 mt-1">
          Who needs you today, and how the class is growing
        </p>
      </div>

      {/* Status blocks — the daily questions */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Not started */}
        <div className="card-surface p-5" data-testid="insights-not-started">
          <h3 className="font-heading text-base font-semibold text-white mb-3 flex items-center gap-2">
            <AlertTriangle className="text-gold-bright" size={18} />
            Hasn't started
          </h3>
          {!status ? (
            <p className="text-slate-500 text-sm">Loading…</p>
          ) : status.perAssignment.every(pa => pa.notStarted.length === 0) ? (
            <p className="text-slate-400 text-sm">Everyone with an active assignment has a draft going. 🎉</p>
          ) : (
            <div className="space-y-3">
              {status.perAssignment.filter(pa => pa.notStarted.length > 0).map(pa => (
                <div key={pa.assignment.id}>
                  <p className="text-slate-400 text-xs uppercase tracking-wide mb-1">{pa.assignment.title}</p>
                  <ul className="space-y-0.5">
                    {pa.notStarted.map((m, i) => (
                      <li key={i} className="text-slate-300 text-sm">{m.full_name || m.email || 'Student'}</li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Stuck */}
        <div className="card-surface p-5" data-testid="insights-stuck">
          <h3 className="font-heading text-base font-semibold text-white mb-3 flex items-center gap-2">
            <Clock className="text-ai-accent" size={18} />
            Stuck (3+ days idle)
          </h3>
          {!status ? (
            <p className="text-slate-500 text-sm">Loading…</p>
          ) : status.stuck.length === 0 ? (
            <p className="text-slate-400 text-sm">No stalled drafts. Momentum looks good.</p>
          ) : (
            <ul className="space-y-2">
              {status.stuck.slice(0, 12).map((d, i) => (
                <li key={i} className="text-sm flex items-baseline justify-between gap-2">
                  <span className="text-slate-300 truncate">
                    {studentLabel(d)}
                    <span className="text-slate-500 text-xs ml-2">{d.assignmentTitle}</span>
                  </span>
                  <span className="text-gold-bright text-xs whitespace-nowrap">{d.idleDays}d idle</span>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Submitted */}
        <div className="card-surface p-5" data-testid="insights-submitted">
          <h3 className="font-heading text-base font-semibold text-white mb-3 flex items-center gap-2">
            <CheckCircle className="text-green-400" size={18} />
            Submitted
          </h3>
          {!status ? (
            <p className="text-slate-500 text-sm">Loading…</p>
          ) : status.perAssignment.length === 0 ? (
            <p className="text-slate-400 text-sm">No active assignments yet.</p>
          ) : (
            <div className="space-y-2">
              {status.perAssignment.map(pa => (
                <div key={pa.assignment.id} className="flex items-baseline justify-between gap-2 text-sm">
                  <span className="text-slate-300 truncate">{pa.assignment.title}</span>
                  <span className="text-green-400 text-xs whitespace-nowrap">
                    {pa.submitted.length} in / {pa.total} started
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <h3 className="font-heading text-lg font-semibold text-white flex items-center gap-2 pt-2">
        <TrendingUp className="text-teal-flow" size={20} />
        Growth
      </h3>

          {/* Summary Stats */}
          <div className="grid grid-cols-3 gap-4">
            <div className="card-surface p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-gold-bright/20 rounded-lg">
                  <BarChart3 className="text-gold-bright" size={20} />
                </div>
                <div>
                  <p className="text-2xl font-bold text-white">{analytics?.total_feedback_items || 0}</p>
                  <p className="text-slate-400 text-sm">Total Feedback Items</p>
                </div>
              </div>
            </div>
            <div className="card-surface p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-ai-accent/20 rounded-lg">
                  <Users className="text-ai-accent" size={20} />
                </div>
                <div>
                  <p className="text-2xl font-bold text-white">{analytics?.students_with_feedback || 0}</p>
                  <p className="text-slate-400 text-sm">Students with Feedback</p>
                </div>
              </div>
            </div>
            <div className="card-surface p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-teal-flow/20 rounded-lg">
                  <TrendingUp className="text-teal-flow" size={20} />
                </div>
                <div>
                  <p className="text-2xl font-bold text-white">{avgMastery !== null ? `${avgMastery}%` : '\u2014'}</p>
                  <p className="text-slate-400 text-sm">Avg. Standards Mastery</p>
                </div>
              </div>
            </div>
          </div>

          {/* CCSS Radar Chart */}
          <div className="card-surface p-6">
            <h3 className="font-heading text-lg font-semibold text-white mb-4 flex items-center gap-2">
              <Target className="text-ai-accent" size={20} />
              CCSS Standards Mastery
            </h3>
            <div className="h-80">
              {radarData.length === 0 ? (
                <div className="h-full flex items-center justify-center">
                  <div className="text-center">
                    <DraftyMascot size="md" mood="thinking" className="mx-auto mb-4" />
                    <p className="text-slate-400">No analytics data yet. Data populates as students receive AI feedback on their writing.</p>
                  </div>
                </div>
              ) : (
                <ResponsiveContainer width="100%" height="100%">
                  <RadarChart data={radarData}>
                    <PolarGrid stroke="#334155" />
                    <PolarAngleAxis dataKey="standard" tick={{ fill: BRAND.textSecondaryDark, fontSize: 12 }} />
                    <PolarRadiusAxis tick={{ fill: '#64748b' }} />
                    <Radar
                      name="Mastery"
                      dataKey="mastery"
                      stroke={BRAND.tealFlow}
                      fill={BRAND.tealFlow}
                      fillOpacity={0.3}
                    />
                    <Radar
                      name="Needs Work"
                      dataKey="needs_work"
                      stroke={BRAND.goldBright}
                      fill={BRAND.goldBright}
                      fillOpacity={0.3}
                    />
                    <Legend />
                  </RadarChart>
                </ResponsiveContainer>
              )}
            </div>
          </div>

          {/* Growth areas + student x standard heatmap (Phase 6 item 4) */}
          <GrowthAreas areas={analytics?.growth_areas} />
          <StandardsHeatmap heatmap={analytics?.standards_heatmap} />

          {/* Standards Breakdown Table -- grouped by anchor */}
          {radarData.length > 0 && (
            <div className="card-surface p-6">
              <h3 className="font-heading text-lg font-semibold text-white mb-4 flex items-center gap-2">
                <FileText className="text-teal-flow" size={20} />
                Standards Breakdown
              </h3>
              <div className="space-y-2">
                {Object.entries(grouped).map(([anchor, { anchorData, subItems }]) => {
                  const stdDef = CCSS_ELA_STANDARDS[anchor];
                  const hasDefinedSubs = stdDef && Object.keys(stdDef.subs).length > 0;
                  const hasDataSubs = subItems.length > 0;
                  const isExpandable = hasDefinedSubs || hasDataSubs;
                  const expanded = expandedAnchors[anchor];

                  const anchorMastery = anchorData
                    ? anchorData.mastery
                    : subItems.length > 0
                      ? Math.round(subItems.reduce((s, i) => s + (i.mastery || 0), 0) / subItems.length)
                      : null;
                  const anchorNeedsWork = anchorData
                    ? anchorData.needs_work
                    : subItems.length > 0
                      ? Math.round(subItems.reduce((s, i) => s + (i.needs_work || 0), 0) / subItems.length)
                      : null;

                  const allSubCodes = new Set([
                    ...subItems.map(si => si.standard),
                    ...(stdDef ? Object.keys(stdDef.subs) : [])
                  ]);
                  const subDataMap = {};
                  for (const si of subItems) subDataMap[si.standard] = si;

                  return (
                    <div key={anchor}>
                      <div
                        className={`flex items-center justify-between p-3 bg-slate-900/50 rounded-lg border border-slate-700 ${isExpandable ? 'cursor-pointer hover:bg-slate-800/70' : ''}`}
                        onClick={isExpandable ? () => toggleAnchorExpand(anchor) : undefined}
                      >
                        <div className="flex items-center gap-2">
                          {isExpandable && (
                            <span className="text-slate-400">
                              {expanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                            </span>
                          )}
                          <span className="badge-ccss">{anchor}</span>
                        </div>
                        <div className="flex items-center gap-6">
                          <ProgressBar value={anchorMastery} color="bg-teal-flow" />
                          <ProgressBar value={anchorNeedsWork} color="bg-gold-bright" />
                        </div>
                      </div>

                      {expanded && isExpandable && (
                        <div className="space-y-1 mt-1">
                          {[...allSubCodes].sort().map(subCode => {
                            const data = subDataMap[subCode];
                            return (
                              <StandardRow
                                key={subCode}
                                label={subCode}
                                mastery={data?.mastery}
                                needsWork={data?.needs_work}
                                indent
                                muted={!data}
                              />
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* One student across assignments (Phase 6 item 4) */}
          <StudentTrajectory students={analytics?.standards_heatmap?.students} />

          {/* Recent Feedback Log */}
          <div className="card-surface p-6">
            <h3 className="font-heading text-lg font-semibold text-white mb-4 flex items-center gap-2">
              <Sparkles className="text-ai-accent" size={20} />
              Recent Feedback
            </h3>
            {analytics?.recent_feedback?.length > 0 ? (
              <div className="space-y-3">
                {analytics.recent_feedback.slice(0, 10).map((fb, idx) => (
                  <div key={idx} className="flex items-center justify-between py-2 border-b border-slate-700 last:border-0">
                    <div className="flex items-center gap-3">
                      <span className="badge-ccss">{fb.ccss_standard || 'General'}</span>
                      <span className="text-white text-sm">{fb.feedback_type === 'comprehensive' ? 'AI coaching feedback' : 'Feedback'}</span>
                    </div>
                    <span className="text-slate-500 text-xs">{new Date(fb.created_at).toLocaleString()}</span>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-6">
                <p className="text-slate-500 text-sm">Feedback entries will appear here as students use the AI writing coach.</p>
              </div>
            )}
          </div>
    </div>
  );
};
